import {ApolloClient, useApolloClient} from '@apollo/client';
import {storage, useShowId} from '@backstage-components/base';
import {useGuestConsent} from '@backstage-components/guest-consent-provider';
import type {UIManager} from '@lcdigital/lst-player-ui';
import type {PlayerAPI, PlayerConfig, PlayerEvent} from 'bitmovin-player';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type MutableRefObject,
  type Reducer,
} from 'react';
import type {VideoComponentDefinition} from './Video.interfaces';
import {
  BitmovinRequestType,
  fixSafariTabulation,
  timeSince,
} from './Video.utils';
import {GetSelf, GetSelfIdQuery, GetSelfQuery} from './gql';
import {TimelineMarker} from '@lcdigital/lst-player-ui/dist/js/framework/uiconfig';
import {DEFAULT_CHROMECAST_RECEIVER_APPLICATION_ID} from './VideoDefinition';

const timelineMarkerClass = 'timeline-marker';

interface HandlePlayerInterface {
  $player: MutableRefObject<HTMLDivElement | null>;
  useCbls?: boolean;
  setDrmLicenseLoaded: (value: boolean) => void;
  defaultSubtitleLangCode: string;
  startTime?: Date;
  timeServiceUrl?: string;
  player?: PlayerAPI;
  uiManager?: UIManager;
  timelineMarkers?: TimelineMarker[];
}

export type UsePlayerResult =
  | {
      drmToken?: string;
      playerApi: PlayerAPI;
      uiManager?: UIManager;
    }
  | undefined;

/**
 * Releases the `PlayerAPI` resources stored in the `draft` object
 */
function destroyExistingPlayer(
  draft: Partial<Exclude<UsePlayerResult, undefined>>
): void {
  if (draft.playerApi) {
    try {
      draft.playerApi.destroy();
    } catch (reason) {
      // ignore the error if it is because the `playerApi` has already been
      // destroyed but rethrow if it is not
      if (!isPlayerApiNotAvailable(reason)) {
        throw reason;
      }
    }
  }
  if (draft.uiManager) {
    draft.uiManager.release();
  }
}

type ConsentStatus = ReturnType<typeof useGuestConsent>;

interface UsePlayerState extends Partial<Exclude<UsePlayerResult, undefined>> {
  /** Whether or not the guest has consented to analytics */
  analyticsConsent?: ConsentStatus;
  /** JWT used to decode content with server side forensic watermarking */
  forensicToken?: string;
  /** Identifier for the guest viewing video */
  guestId?: string;
}

type UsePlayerAction =
  | {type: 'DESTROY'}
  | {type: 'SET_SELF'; drmToken: string; forensicToken?: string; id: string}
  | {type: 'SET_PLAYER'; playerApi: PlayerAPI; uiManager?: UIManager};

/**
 * Collect `PlayerAPI` and `UIManager` into a state object with the DRM token.
 * The `DESTROY` action releases the resources from the `PlayerAPI`.
 */
const playerReducer: Reducer<UsePlayerState, UsePlayerAction> = (
  draft,
  action
) => {
  switch (action.type) {
    case 'DESTROY': {
      destroyExistingPlayer(draft);
      return {drmToken: draft.drmToken};
    }
    case 'SET_PLAYER':
      destroyExistingPlayer(draft);
      return {
        ...draft,
        playerApi: action.playerApi,
        uiManager: action.uiManager,
      };
    case 'SET_SELF':
      // Only update state if the token has changed
      if (draft.drmToken !== action.drmToken) {
        return {
          ...draft,
          drmToken: action.drmToken,
          forensicToken: action.forensicToken,
          guestId: action.id,
        };
      } else {
        return draft;
      }
  }
};

/**
 * Create the Bitmovin player instance and the UIManager to control it for the
 * given player element and video module definition.
 */
export const usePlayer = (
  $player: MutableRefObject<HTMLDivElement | null>,
  definition: VideoComponentDefinition,
  defaultSubtitleLangCode: string
): UsePlayerResult => {
  const hasNewUi = definition.props.advancedOptions.newUi;
  const client = useApolloClient();
  const showId = useShowId();
  // Store `analyticsConsent` in a ref; the consent value available when the
  // video player loads is the consent used for the lifetime of the player. The
  // value in the Ref does not get updated.
  const analyticsConsent = useRef(useGuestConsent('analytics'));
  const [isSelfLoaded, setIsSelfLoaded] = useState(false);
  const [state, dispatch] = useReducer(playerReducer, {});
  const needsTokens =
    !('progressive' in definition.props.source) &&
    (definition.props.source.serversideForensic ||
      definition.props.source.drmEnabled ||
      false);
  const forensicToken = useMemo(() => state.forensicToken, [state]);
  const guestId = useMemo(
    () => (analyticsConsent.current === 'accepted' ? state.guestId : undefined),
    [state]
  );
  const tokenInformation = useMemo(
    () => ({
      analyticsConsent: analyticsConsent.current,
      forensicToken,
      guestId,
    }),
    [forensicToken, guestId]
  );

  useEffect(() => {
    const currentPlayerElement = $player.current;
    /** A flag indicating whether the effect has up-to-date values in its closure */
    let isAborted = false;

    // If the player element ref hasn't been set yet, bail.
    if (currentPlayerElement === null || !isSelfLoaded) return;

    Promise.all([
      import('bitmovin-player'),
      import('@lcdigital/lst-player-ui'),
    ]).then(([bm, lst]) => {
      // Don't dispatch changes if this effect has been aborted
      if (isAborted) return;
      const {Player} = bm;

      const playerConfig = createPlayerConfig(definition, tokenInformation);
      const p = new Player(currentPlayerElement, playerConfig);

      // If newUi is set, don't load the legacy UI
      let uim = undefined;
      if (!hasNewUi) {
        const {UIFactory} = lst;
        uim = UIFactory.buildDefaultUI(p, {}, {defaultSubtitleLangCode});
      }

      dispatch({type: 'SET_PLAYER', playerApi: p, uiManager: uim});
    });
    // Set a flag indicating this effect no longer has up-to-date values in
    // its closure in the destroy callback
    return () => {
      isAborted = true;
    };
  }, [
    $player,
    defaultSubtitleLangCode,
    definition,
    hasNewUi,
    isSelfLoaded,
    tokenInformation,
  ]);
  // clean up player resources on unmount
  useEffect(() => () => dispatch({type: 'DESTROY'}), []);

  // listen for `GuestAuth:success` custom event
  useEffect(() => {
    /** A flag indicating whether the effect has up-to-date values in its closure */
    let isAborted = false;
    // Refetch pages on access token change
    const refreshSecurityTokens = (): void => {
      querySelf(client, showId, needsTokens).then((self) => {
        // Don't dispatch changes if this effect has been aborted
        if (isAborted) return;
        // Self has been loaded the first time so the player can start loading
        setIsSelfLoaded(true);
        // Don't dispatch changes if there is no token
        if (self === null || !('drmToken' in self)) return;
        dispatch({type: 'SET_SELF', ...self});
      });
    };
    // Immediately refresh the token
    refreshSecurityTokens();
    // Set up listeners for access change
    document.body.addEventListener('GuestAuth:success', refreshSecurityTokens);
    return () => {
      isAborted = true;
      document.body.removeEventListener(
        'GuestAuth:success',
        refreshSecurityTokens
      );
    };
  }, [client, needsTokens, showId]);

  const result = useMemo<UsePlayerResult>(() => {
    // If we're using the new UI, we don't need to check the uiManager status
    const uiManagerReady = hasNewUi ? true : !!state.uiManager;

    if (state.playerApi && uiManagerReady) {
      return {
        drmToken: state.drmToken,
        playerApi: state.playerApi,
        uiManager: state.uiManager,
      };
    } else {
      return undefined;
    }
  }, [state, hasNewUi]);

  return result;
};

/**
 * Read details from the `self` query, used to retrieve DRM and Serverside
 * Forensic Watermarking tokens that are specific to a particular guest.
 * @param needsTokens optional. If false, don't fetch drmToken or forensicToken.
 */
async function querySelf(
  client: ApolloClient<object>,
  showId: string,
  needsTokens = true
): Promise<GetSelf['self']> {
  if (needsTokens === false) {
    const response = await client.query<GetSelf>({
      query: GetSelfIdQuery,
      context: {showId},
      fetchPolicy: 'no-cache',
    });
    return response.data.self;
  }
  const response = await client.query<GetSelf>({
    query: GetSelfQuery,
    context: {showId},
    fetchPolicy: 'no-cache',
  });
  return response.data.self;
}

const lowLatencyConfig = {
  tweaks: {
    RESTART_THRESHOLD: 0.2,
    RESTART_THRESHOLD_DELTA: 0.05,
    STARTUP_THRESHOLD: 0.2,
    STARTUP_THRESHOLD_DELTA: 0.05,
    END_OF_BUFFER_TOLERANCE: 0.05,
    LIVE_EDGE_DISTANCE: 0.5,
    LOW_LATENCY_BUFFER_GUARD: 0.8,
    CHUNKED_CMAF_STREAMING: true,
  },
  live: {
    lowLatency: {
      targetLatency: 4,
      catchup: {
        playbackRateThreshold: 0.075,
        seekThreshold: 5,
        playbackRate: 1.2,
      },
      fallback: {
        playbackRateThreshold: 0.075,
        seekThreshold: 5,
        playbackRate: 0.95,
      },
    },
  },
};

type TokenInformation = Pick<
  UsePlayerState,
  'analyticsConsent' | 'forensicToken' | 'guestId'
>;

/**
 * Create a bitmovin player configuration from the video module definition.
 */
function createPlayerConfig(
  definition: VideoComponentDefinition,
  tokenInformation: TokenInformation
): PlayerConfig {
  const bitmovinKey =
    definition.config.scope === 'attendee' ? definition.config.bitmovinKey : '';
  const bitmovinAnalyticsKey =
    definition.config.scope === 'attendee'
      ? definition.config.bitmovinAnalyticsKey
      : '';
  const {autoplay, playerConfig, muted} = definition.props;
  const requiresForensic =
    !('progressive' in definition.props.source) &&
    definition.props.source.serversideForensic;
  const applyLowLatency =
    definition.props.advancedOptions.latencyConfig === 'Lower Latency';

  const {analyticsConsent, forensicToken, guestId} = tokenInformation;
  const playerNetworkConfig: PlayerConfig['network'] = {
    preprocessHttpRequest(type, request) {
      if (
        requiresForensic &&
        forensicToken &&
        // Don't add the pattern token to any DRM requests
        type !== BitmovinRequestType.DRM_CERTIFICATE_FAIRPLAY &&
        type !== BitmovinRequestType.DRM_LICENSE_CLEARKEY &&
        type !== BitmovinRequestType.DRM_LICENSE_FAIRPLAY &&
        type !== BitmovinRequestType.DRM_LICENSE_PLAYREADY &&
        type !== BitmovinRequestType.DRM_LICENSE_PRIMETIME &&
        type !== BitmovinRequestType.DRM_LICENSE_WIDEVINE
      ) {
        request.headers.jwt = forensicToken;
      }
      return Promise.resolve(request);
    },
    preprocessHttpResponse: (type, response) => {
      const newResponse = {...response};
      // Patches a bug in Bitmovin player for forced narrative subtitle tracks.
      // Bitmovin currently looks for `forced_subtitle`, when the DASH standard
      // designates `forced-subtitle` as the valid role value.
      if (type === 'manifest/dash' && typeof response.body === 'string') {
        // @ts-expect-error newResponse.body throws a generic error here due to an issue with the bitmovin player typings
        newResponse.body = response.body.replaceAll(
          'forced-subtitle',
          'forced_subtitle'
        );
      }

      return Promise.resolve(newResponse);
    },
  };

  return {
    key: bitmovinKey,
    playback: {
      autoplay: autoplay,
      timeShift: false,
      muted,
    },
    analytics:
      analyticsConsent === 'accepted'
        ? {
            key: bitmovinAnalyticsKey,
            videoId: definition.id,
            userId: guestId,
          }
        : false,
    network: playerNetworkConfig,
    remotecontrol: {
      type: 'googlecast',
      receiverApplicationId:
        definition.props.chromecastReceiverId ??
        DEFAULT_CHROMECAST_RECEIVER_APPLICATION_ID,
    },
    ui: false, // this will disable the built-in UI
    logs: {
      bitmovin: false,
    },
    tweaks: {
      fairplay_ignore_duplicate_init_data_key_errors: true,
    },
    ...(applyLowLatency && lowLatencyConfig), // If low latency is set, add the lowLatencyConfig
    ...playerConfig,
  };
}

export const useHandlePlayer = ({
  $player,
  player,
  useCbls,
  setDrmLicenseLoaded,
  defaultSubtitleLangCode,
  startTime,
  timeServiceUrl,
  uiManager,
  timelineMarkers,
}: HandlePlayerInterface): void => {
  useSafariFixes(player, defaultSubtitleLangCode);

  const handleSeek = useCallback(async (): Promise<void> => {
    if (!player) return;

    if (startTime) {
      const timestamp = await timeSince(startTime, true, timeServiceUrl);
      player.seek(timestamp);
    }
  }, [player, startTime, timeServiceUrl]);

  // Call `handleSeek` if `startTime` or `useCbls` changes, and `useCbls` is true.
  useEffect(() => {
    if (useCbls) {
      handleSeek();
    }
  }, [handleSeek, startTime, useCbls]);

  useEffect(() => {
    if (!$player.current || !player) {
      return;
    }
    const handleMutedChange = (): void => {
      storage.setItem('bmPlayerMuted', String(player.isMuted()));
    };
    const handleUnmutedChange = (): void => {
      storage.setItem('bmPlayerMuted', 'false');
    };
    const handleVolumeChange = (): void => {
      if (storage.getItem('bmPlayerMuted')) {
        handleUnmutedChange();
      }
      storage.setItem('bmPlayerVolume', String(player.getVolume()));
    };

    if (useCbls) {
      player.on('play' as PlayerEvent.Play, handleSeek);
    }

    // Handle drmLicenseLoaded state on license
    player.on('drmlicenseadded' as PlayerEvent.DrmLicenseAdded, () =>
      setDrmLicenseLoaded(true)
    );
    // Handle volume change event
    player.on('volumechanged' as PlayerEvent.VolumeChanged, handleVolumeChange);
    player.on('muted' as PlayerEvent.Muted, handleMutedChange);
    player.on('unmuted' as PlayerEvent.Unmuted, handleUnmutedChange);
    // Set initial volume to bmPlayerVolume || 100;
    if (storage.getItem('bmPlayerMuted') === 'true') {
      player.mute();
    }
    player.setVolume(Number(storage.getItem('bmPlayerVolume')) || 100);
    // Handle adding timeline markers
    player.on('ready' as PlayerEvent.Ready, () => {
      timelineMarkers?.forEach((marker) => {
        if (uiManager) {
          marker.cssClasses = [timelineMarkerClass];
          uiManager.addTimelineMarker(marker);
        }
      });
    });
    // Forcibly add the `bitmovinplayer-container` class to the `<div>`
    // containing the `<video>` element
    // WARNING: This is reliant on bitmovin-player library implementation
    // regarding how the DOM is structured and is brittle to non-API changes in
    // the bitmovin-player library
    player.on('sourceloaded' as PlayerEvent.SourceLoaded, () => {
      const video = player.getVideoElement();
      const container = video?.parentElement;
      if (
        !container?.classList.contains('bitmovinplayer-container') &&
        !container?.classList.contains('aspect-16x9')
      ) {
        container?.classList.add('bitmovinplayer-container', 'aspect-16x9');
      }
    });
  }, [
    $player,
    defaultSubtitleLangCode,
    startTime,
    timeServiceUrl,
    setDrmLicenseLoaded,
    useCbls,
    player,
    handleSeek,
    uiManager,
    timelineMarkers,
  ]);
};

/**
 * Handle Safari Tabindex update as player loads new buttons and check for
 * default subtitles.
 */
const useSafariFixes = (
  player: PlayerAPI | undefined,
  defaultSubtitleLangCode: string
): void => {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  useEffect(() => {
    if (!player || !isSafari) {
      return;
    }
    const checkDefaultSubtitlesSafari = (): void => {
      if (!player || !defaultSubtitleLangCode) return;
      const filtered = player.subtitles?.list().filter(function (sub) {
        return sub.lang.toLowerCase() === defaultSubtitleLangCode;
      });
      if (filtered && filtered[0]) {
        player?.subtitles?.enable(filtered[0].id);
      }
    };
    player.on(
      'subtitleadded' as PlayerEvent.SubtitleAdded,
      checkDefaultSubtitlesSafari
    );
    player.on(
      'subtitleadded' as PlayerEvent.SubtitleAdded,
      fixSafariTabulation
    );
    player.on('sourceloaded' as PlayerEvent.SourceLoaded, () => {
      // Hide seekbar if live (workaround for safari hls live stream seekbar bug)
      if (player?.isLive()) {
        Array.from(document.getElementsByClassName('bmpui-ui-seekbar')).forEach(
          (element) => {
            element.className += ' bmpui-hidden';
          }
        );
      }
      fixSafariTabulation();
    });
    player.on(
      'airplayavailable' as PlayerEvent.AirplayAvailable,
      fixSafariTabulation
    );
    player.on('audioadded' as PlayerEvent.AudioAdded, fixSafariTabulation);
    return () => {
      try {
        player.off(
          'subtitleadded' as PlayerEvent.SubtitleAdded,
          checkDefaultSubtitlesSafari
        );
        player.off(
          'subtitleadded' as PlayerEvent.SubtitleAdded,
          fixSafariTabulation
        );
        player.off(
          'sourceloaded' as PlayerEvent.SourceLoaded,
          fixSafariTabulation
        );
        player.off(
          'airplayavailable' as PlayerEvent.AirplayAvailable,
          fixSafariTabulation
        );
        player.off('audioadded' as PlayerEvent.AudioAdded, fixSafariTabulation);
      } catch (reason) {
        if (isPlayerApiNotAvailable(reason)) {
          // ignore player not available error, if the player is destroyed it
          // is fine to faile in removing event handlers
        } else {
          throw reason;
        }
      }
    };
  }, [defaultSubtitleLangCode, player, isSafari]);
};

/**
 * Check if the given `reason` for throwing is `PlayerError` corresponding to
 * the `PlayerAPI` having been destroyed.
 */
export function isPlayerApiNotAvailable(reason: unknown): boolean {
  if (
    typeof reason === 'object' &&
    reason !== null &&
    'code' in reason &&
    typeof reason.code === 'number'
  ) {
    return reason.code === 1001;
  } else {
    return false;
  }
}
